import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Message, SpinnerService } from 'shared-components';
import { HistoricoContextService } from "../../context/historico-context.service";
import { HistoricoService } from "../../services/historico.service";
import { Router } from "@angular/router";

@Component({
    selector: 'app-historico-cultivos',
    templateUrl: './historico-cultivos.component.html'
})
export class HistoricoCultivosComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

    private _filters;
    public filtersVisible = true;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;

    public historicoCultivos;
    public denominaciones;
    public campanyas;

    public paisId;

    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledCampanyas: Subject<boolean> = new Subject();

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public dropdownInfo: number;
    public dropdownSection: number;
    public pestanaActiva = new Array<number>();

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public orderBy: string;
    public columnId: number;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _sessionContext: SessionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly _historicoService: HistoricoService,
        private readonly _historicoContext: HistoricoContextService,
        private readonly _dataProvider: DataProvider
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            denominacion: [{ value: '', disabled: true }, Validators.required],
            campanya: [{ value: '', disabled: true }, Validators.required],
            desProductor: [''],
            desFinca: ['', Validators.required],
        });

        this._historicoContext.historicoCultivosFilters = null;

        this.checkWarnings(this.filtersForm.get('pais').value);

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this.paisId = pais.id;
                this.getDenominacionesByPais(pais.id);
            } else {
                this.disabledDenominaciones.next(true);
                this.denominaciones = [];
                this.filtersForm.get('denominacion').patchValue('');
                this.filtersForm.get('denominacion').disable();
            }
        });

        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.filtersForm.get('pais').enabled) {
                this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
                this.filtersForm.get('pais').disable();
            }
        }

        this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.getCampanyasByDenominacion(denominacion.id, this.paisId);
            } else {
                this.disabledCampanyas.next(true);
                this.campanyas = [];
                this.filtersForm.get('campanya').patchValue('');
                this.filtersForm.get('campanya').disable();
            }
        });

        this.filtersForm.get('campanya').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((campanya) => {
            if (campanya) {
                this.filtersForm.get('desFinca').clearValidators();
                this.filtersForm.get('desFinca').updateValueAndValidity();
            } else {
                this.filtersForm.get('desFinca').setValidators(Validators.required);
                this.filtersForm.get('desFinca').updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
        });

        this.filtersForm.get('desFinca').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((des) => {
            if (des) {
                this.filtersForm.get('campanya').clearValidators();
                this.filtersForm.get('campanya').updateValueAndValidity();
            } else {
                this.filtersForm.get('campanya').setValidators(Validators.required);
                this.filtersForm.get('campanya').updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get paises() {
        return this._dataProvider.paises;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getDenominacionesByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getDenominaciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.denominaciones = resp.lista;
                if (this.denominaciones.length === 1) {
                    this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
                    this.filtersForm.get('denominacion').disable();
                    this.disabledDenominaciones.next(true);
                } else {
                    this.filtersForm.get('denominacion').enable();
                    this.disabledDenominaciones.next(false);
                }
            }
        });
    }

    public getCampanyasByDenominacion(denominacionId, paisId) {
        let filters = {
            denominacionId: denominacionId,
            paisId: paisId,
            volcadoHistorico: true
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getCampanyas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.campanyas = resp.lista;
                this.campanyas.forEach(campanya => {
                    campanya.des = campanya.anyo.toString();
                });
                if (this.campanyas.length === 1) {
                    this.filtersForm.get('campanya').patchValue(this.campanyas[0]);
                } else {
                    this.filtersForm.get('campanya').enable();
                    this.disabledCampanyas.next(false);
                }
            }
        });
    }

    private checkWarnings(campanya) {
        if (campanya && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getHistoricoCultivos();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.campanyaOFinca' });
        }
    }

    public pageChange(page) {
        this.dropdownInfo = -1;
        this.dropdownSection = -1;
        this._historicoContext.historicoCultivosFilters.numPagina = this.currentPage = page - 1;
        this.getHistoricoCultivos();
    }

    public itemsPerPageChange(items) {
        this.dropdownInfo = -1;
        this.dropdownSection = -1;
        this._historicoContext.historicoCultivosFilters.numPagina = this.currentPage = 0;
        this._historicoContext.historicoCultivosFilters.tamPagina = this.itemsPerPage = items;
        this.getHistoricoCultivos();
    }

    public checkValuesForm(): boolean {
        return (
            (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
            (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled) ||
            this.filtersForm.get('campanya').value ||
            this.filtersForm.get('desProductor').value ||
            this.filtersForm.get('desFinca').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.get('pais').disabled ? this.filtersForm.get('pais').patchValue(this.filtersForm.get('pais').value) : this.filtersForm.controls['pais'].reset();
        this.filtersForm.controls['desFinca'].reset();
        this.filtersForm.controls['desProductor'].reset();

        this.checkWarnings(this.filtersForm.get('pais').value);
    }

    public filtrar() {
        if (!this.filtersForm.get('campanya').value && !this.filtersForm.get('desFinca').value) {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.info.campanyaOFinca' });
        }

        if (Object.keys(this.filtersForm.controls)
            .filter(key => key !== 'campanya' && key !== 'desFinca')
            .some(key => this.filtersForm.get(key).hasError('required'))) {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.required' });
        }

        if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._historicoContext.historicoCultivosFilters = {
            campanyaId: this.filtersForm.get('campanya').value ? this.filtersForm.get('campanya').value.id : null,
            desProductor: this.filtersForm.get('desProductor').value ? this.filtersForm.get('desProductor').value : '',
            desFinca: this.filtersForm.get('desFinca').value ? this.filtersForm.get('desFinca').value : null,
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage
        };
        this.getHistoricoCultivos();
    }

    public getHistoricoCultivos() {
        if (this._historicoContext.historicoCultivosFilters) {
            this._filters = this._historicoContext.historicoCultivosFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._historicoService.getHistoricoCultivos(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.historicoCultivos = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public exportarPdfOrExcel(tipo) {
        if (this.historicoCultivos) {
            if (this._historicoContext.historicoCultivosFilters) {
                var mimeType;
                this._filters = this._historicoContext.historicoCultivosFilters;
                this._filters.tamPagina = 0;
                if (tipo === 'excel') {
                    this._filters.tipoArchivo = 'Excel';
                    mimeType = 'data:application/vnd.ms-excel;base64,';
                } else {
                    this._filters.tipoArchivo = 'Pdf';
                    mimeType = 'data:application/pdf;base64,';
                }
            }
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._historicoService.getRepHistoricoCultivos(this._filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                const downloadLink = document.createElement('a');
                downloadLink.href = mimeType + resp.base64Archivo;
                downloadLink.download = resp.nombreArchivo;
                downloadLink.click();
            },(error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.file' });
            });
        } else {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.exportNoData' });
        }
    }

    public showDetails(i: number) {
        this.dropdownInfo = this.dropdownInfo === i ? -1 : i;
        // Desplazamiento a la izquierda para tablet (mismo tamaño máx. que en table.scss)
        if (this.dropdownInfo !== -1 && window.innerWidth <= 1199) {
            // Retardo en caso de que haya otro panel abierto
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public getRolePermission() {
        let disabled = false;

        if (this.role === 'Administrador') {
            disabled = false;
        } else if (this.role === 'Auditor') {
            disabled = false;
        } else if (this.role === 'Supervisor') {
            disabled = false;
        }

        return disabled;
    }

    public getIcon(prop: string) {
        let iconClass = 'fa fa-sort';
        if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty === prop) {
            iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
        this.columnId = idColumn;
        if (this.historicoCultivos && this.historicoCultivos.length > 0) {
            if (this._historicoContext.historicoCultivosFilters) {
                this._filters = this._historicoContext.historicoCultivosFilters;
                if (!this.orderBy || this.orderBy.includes('DESC')) {
                    this.orderBy = 'ASC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: false
                        }
                    ];
                } else if (this.orderBy.includes('ASC')) {
                    this.orderBy = 'DESC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: true
                        }
                    ];
                }
                this.getHistoricoCultivos();
            }
        }
    }

    public openDocHelp() {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._historicoService.getDocHelp('History_Crops.pdf').toPromise().finally(() => {
          this.checkCloseSpinner();
      }).then((resp) => {
          localStorage.setItem('base64', resp.base64Archivo);
          const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
          window.open(url, '_blank');
      });
    }

    public trackByCultivoId(index: number, historico: { cultivoId: number }): number {
      return historico.cultivoId;
    }
}
